/**
 * Display rules for a `GET …/{uuid}/assignment-options` picker (DNKADM-0260).
 *
 * Extracted verbatim from `ReassignAssignmentDialog` so the calendar's reassignment
 * dialog and the Confirmations screen's Change Resource/Staff dialog share ONE copy
 * of these rules rather than two that drift. The two dialogs stay separate — they
 * serve different screens and different save semantics (`PUT …/assignment` vs
 * `PUT …/details`) — but what an option LOOKS like must not depend on which screen
 * you opened it from.
 *
 * Pure and component-free, so it lives in a `.ts` module (no
 * `react-refresh/only-export-components` exposure) and is unit-testable directly.
 *
 * The conflicts-SUMMARY block is deliberately NOT here: it reads several pieces of
 * dialog-local state, and each dialog's state shape differs. Each mirrors that logic
 * locally rather than forcing a shared abstraction over two different shapes.
 */
import type { AssignmentOptions } from '@/api/reservations';

/** One picker option (the exported `AssignmentOptions` is the only public shape). */
export type Option = AssignmentOptions['staff'][number];

/**
 * The id to show as selected for a dimension: the endpoint's `currentlyAssigned`
 * option, else the reservation's own id when it is actually in the pool (a stale
 * id would otherwise preselect nothing and make an unchanged pick look changed).
 */
export function currentIdOf(list: Option[] | undefined, fallback: string | null): string {
  const flagged = list?.find((o) => o.currentlyAssigned);
  if (flagged) return flagged.id;
  if (fallback !== null && list?.some((o) => o.id === fallback)) return fallback;
  return '';
}

/** A dimension is offered only when its mode is not `none` (⇒ a non-empty pool). */
export function isOffered(mode: AssignmentOptions['staffMode'], list: Option[]): boolean {
  return mode !== 'none' && list.length > 0;
}

/** The option label — conflicted options are marked, never hidden or disabled. */
export function optionLabel(option: Option): string {
  const name = option.name.trim() || option.id;
  return option.available ? name : `${name} (conflict)`;
}
